<template>
  <div
    aria-live="assertive"
    aria-atomic="false"
    class="fixed z-50 bottom-6 right-6 left-6 lg:top-8 lg:right-8 lg:bottom-auto lg:left-auto lg:w-[32em]"
    :class="{'pointer-events-none' : hasNoNotifications }">
    <TransitionGroup
      tag="ol"
      class="w-full flex flex-col lg:flex-col-reverse gap-4"
      :aria-hidden="hasNoNotifications"
      move-class="motion-safe:transition-all motion-safe:duration-300 motion-safe:ease-out"
      enter-active-class="motion-safe:transition-all motion-safe:duration-300 motion-safe:ease-out "
      enter-from-class="motion-safe:opacity-0 motion-safe:translate-y-full lg:motion-safe:translate-y-0 lg:motion-safe:translate-x-full"
      enter-to-class="motion-safe:opacity-100 motion-safe:translate-y-0 lg:motion-safe:translate-x-0"
      leave-active-class="motion-safe:transition-opacity motion-safe:duration-200"
      leave-from-class="motion-safe:opacity-100"
      leave-to-class="motion-safe:opacity-0">
      <li
        v-for="{ id, variant, headingText, bodyText, slots, isManual } in displayingNotifications"
        :key="id"
        @mouseenter="pauseNotificationTimeout(id)"
        @mouseleave="resumeNotificationTimeout(id)">
        <NotificationComponent
          :variant="variant"
          :heading-text="headingText"
          class="shadow-lg">
          <template
            v-if="bodyText || isManual"
            #body>
            <p
              v-if="bodyText"
              class="mt-2">
              {{ bodyText }}
            </p>
            <ButtonComponent
              v-if="isManual"
              label="Dismiss"
              class="mt-4"
              @custom-click="removeNotification(id)" />
          </template>
          <template
            v-for="(content, name) in slots"
            #[name]
            :key="name">
            <component :is="content" />
            <div
              v-if="isManual && name === 'body'"
              class="mt-4">
              <ButtonComponent
                label="Dismiss"
                @custom-click="removeNotification(id)" />
            </div>
          </template>
        </NotificationComponent>
      </li>
    </TransitionGroup>
  </div>
</template>

<script>

import NotificationComponent from './NotificationComponent.vue'
import ButtonComponent from './ButtonComponent.vue'

export default {
  name: 'ToastComponent',
  components: {
    NotificationComponent,
    ButtonComponent,
  },
  props: {
    /** Maximum number of notifications permitted to be displayed at any one time.  */
    maxVisible: {
      type: Number,
      default: 1, // provide null to set no limit
    },
    /** Time duration in milliseconds that notifications are displayed. Can be overridden per notification. User hover interaction with notification pauses its timer. */
    autoDismissTimeout: {
      type: Number,
      default: 4000,
    },
  },
  data() {
    return {
      notificationCount: 0,
      queuedNotifications: [],
      displayingNotifications: [],
      activeAutoNotifications: {},
      pausedAutoNotifications: {},
    }
  },
  methods: {
    addNotification({ variant, headingText, bodyText, slots = {}, dismissTimeout = this.autoDismissTimeout, isManual = false }) {
      this.notificationCount++
      const id = this.notificationCount
      const notification = { id, variant, headingText, bodyText, slots, dismissTimeout, servedStartTime: 0, servedElapsedTime: 0, isManual }
      if (this.displayingNotifications.length < this.maxVisible || this.maxVisible === null) {
        this.serveNotification(notification)
      }
      else {
        this.queuedNotifications.push(notification)
      }
    },
    serveNotification(notification) {
      this.displayingNotifications.push(notification)
      notification.servedStartTime = this.getTimeNowInMs()
      if (notification.isManual) return
      this.activeAutoNotifications[notification.id] = setTimeout(() => {
        this.removeNotification(notification.id)
      }, notification.dismissTimeout)
    },
    removeNotification(id) {
      const indexToRemove = this.displayingNotifications.findIndex(notification => notification.id === id)
      if (indexToRemove !== -1) this.displayingNotifications.splice(indexToRemove, 1)
      clearTimeout(this.activeAutoNotifications[id])
      delete this.activeAutoNotifications[id]
      delete this.pausedAutoNotifications[id]
      if (this.queuedNotifications.length > 0) {
        const nextNotification = this.queuedNotifications.shift()
        this.serveNotification(nextNotification)
      }
    },
    pauseNotificationTimeout(id) {
      const notification = this.getDisplayingNotification(id)
      if (!notification) return
      if (notification.isManual) return
      clearTimeout(this.activeAutoNotifications[id])
      notification.servedElapsedTime += this.getTimeNowInMs() - (this.pausedAutoNotifications[id] || notification.servedStartTime)
      this.pausedAutoNotifications[id] = this.getTimeNowInMs()
    },
    resumeNotificationTimeout(id) {
      const notification = this.getDisplayingNotification(id)
      if (!notification) return
      if (notification.isManual) return
      if (!this.pausedAutoNotifications[id]) return
      const remainingTimeTillDismiss = notification.dismissTimeout - notification.servedElapsedTime
      this.activeAutoNotifications[id] = setTimeout(() => {
        this.removeNotification(id)
      }, remainingTimeTillDismiss)
      delete this.pausedAutoNotifications[id]
    },
    getDisplayingNotification(id) {
      return this.displayingNotifications.find(notification => notification.id === id)
    },
    getTimeNowInMs() {
      return new Date().getTime()
    },
  },
  computed: {
    hasNoNotifications() {
      return !this.displayingNotifications.length // For screen readers to ignore <ol> when empty on page. Alternative of v-if or <ol> wrapper complicates/disrupts Transitions.
    },
  },
}
</script>
